import { ApiService } from '@cue/api';
import { CollectionViewer } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { NavigationBasicInfo, NavigationBasicInfoWithFirstLetter } from '@cue/data';
import { hasAppId, HasAppId, hasKioskId, HasKioskId } from '@cue/navigation-services';

export type BasicListDataSourceRequest = {
  languageCode: string;
  kioskId: string | null;
  deploymentId: string | null;
  searchString: string;
  selectedAreaIds: number[];
  selectedCategoryIdList: string[];
  selectedResourceTypeIds: number[];
};

export type BasicListDataSourceResponse = {
  total: number;
  data: NavigationBasicInfo[];
  alphabetInfo: {
    letter: string;
    available: boolean;
    firstIndex: number;
    count: number;
  }[];
};

@Injectable({
  providedIn: 'root',
})
export class BasicListDataSourceFactory {
  apiService = inject(ApiService);
  create(params: BasicListDataSourceRequest & (HasAppId | HasKioskId)) {
    let payload = {
      ...params,
      skip: 0,
      take: 0,
    };

    if (hasAppId(params) && params.appId) {
      payload = {
        ...payload,
        appId: params.appId,
      };
    } else if (hasKioskId(params) && params.kioskId) {
      payload = {
        ...payload,
        kioskId: params.kioskId,
      };
    }

    return this.apiService
      .call<any, BasicListDataSourceResponse>({
        url: '/api/navigation/basic-list-v2',
        method: 'post',
        payload,
      })
      .pipe(map((x) => new BasicListDataSource(x.alphabetInfo, this.apiService, 20, x.total, params)));
  }
}

export class BasicListDataSource {
  private _pageSize = 20;
  private _cachedData!: NavigationBasicInfoWithFirstLetter[];
  private readonly _dataStream!: BehaviorSubject<(NavigationBasicInfoWithFirstLetter | undefined)[]>;
  private readonly _subscription = new Subscription();

  private _fetchedPages = new Set<number>();

  connect(collectionViewer: CollectionViewer): Observable<(NavigationBasicInfoWithFirstLetter | undefined)[]> {
    this._subscription.add(
      collectionViewer.viewChange.subscribe((range) => {
        const startPage = this._getPageForIndex(range.start);
        const endPage = this._getPageForIndex(range.end - 1);
        for (let i = startPage; i <= endPage; i++) {
          this._fetchPage(i);
        }
      })
    );
    return this._dataStream;
  }

  private _getPageForIndex(index: number): number {
    return Math.floor(index / this._pageSize);
  }

  disconnect(): void {
    this._subscription.unsubscribe();
  }

  private _fetchPage(page: number) {
    if (this._fetchedPages.has(page)) {
      return;
    }
    this._fetchedPages.add(page);

    // Use `setTimeout` to simulate fetching data from server.
    this.apiService
      .call<any, any>({
        url: '/api/navigation/basic-list-v2',
        method: 'post',
        payload: {
          ...this.params,
          skip: page * this._pageSize,
          take: this._pageSize,
        },
      })
      .subscribe((r: { data: NavigationBasicInfoWithFirstLetter[] }) => {
        this._cachedData.splice(page * this._pageSize, this._pageSize, ...r.data);
        this._dataStream.next(this._cachedData);
      });
  }

  constructor(
    public alphabetInfo: {
      letter: string;
      available: boolean;
      firstIndex: number;
      count: number;
    }[],
    private apiService: ApiService,
    private pageSize: number,
    public total: number,
    private params: BasicListDataSourceRequest & (HasAppId | HasKioskId)
  ) {
    this._cachedData = Array.from<NavigationBasicInfoWithFirstLetter>({ length: this.total });
    this._dataStream = new BehaviorSubject<(NavigationBasicInfoWithFirstLetter | undefined)[]>(this._cachedData);
  }
}
